import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  CircularProgress,
  Paper,
} from '@mui/material';
import { useDeploy } from '../contexts/DeployContext';

function formatDate(dateString) {
  if (!dateString) return '—';
  try {
    const d = new Date(dateString);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    const hours = String(d.getHours()).padStart(2, '0');
    const minutes = String(d.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day} ${hours}:${minutes}`;
  } catch {
    return dateString;
  }
}

function FeedAlgosTable({ selectedFeed, onSelectFeed, refreshKey = 0 }) {
  const { deployApi } = useDeploy();

  const [feeds, setFeeds] = useState([]);
  const [listLoading, setListLoading] = useState(false);
  const [listError, setListError] = useState(null);

  const fetchFeeds = useCallback(async () => {
    setListLoading(true);
    setListError(null);
    try {
      const res = await deployApi.listFeedAlgos();
      const items = res?.feed_algos ?? [];
      setFeeds(items);
    } catch (err) {
      setListError(err?.message ?? 'Failed to load feed algos');
      setFeeds([]);
    } finally {
      setListLoading(false);
    }
  }, [deployApi]);

  useEffect(() => {
    fetchFeeds();
  }, [fetchFeeds, refreshKey]);

  if (listLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (listError) {
    return (
      <Box sx={{ px: 2, py: 1 }}>
        <Typography color="error" variant="body2">{listError}</Typography>
      </Box>
    );
  }

  if (feeds.length === 0) {
    return (
      <Box sx={{ py: 4, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          No algorithms yet. Create one in the right column.
        </Typography>
      </Box>
    );
  }

  return (
    <TableContainer component={Paper} variant="outlined" sx={{ boxShadow: 'none', border: 'none' }}>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell>ID</TableCell>
            <TableCell>Name</TableCell>
            <TableCell>Type</TableCell>
            <TableCell>Inputs</TableCell>
            <TableCell>Created</TableCell>
            <TableCell>Updated</TableCell>
            <TableCell>Public</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {feeds.map((f) => (
            <TableRow
              key={f.algo_id}
              hover
              onClick={() => onSelectFeed(f)}
              selected={selectedFeed?.algo_id === f.algo_id}
              sx={{ cursor: 'pointer' }}
            >
              <TableCell>{f.algo_id}</TableCell>
              <TableCell>{f.name || `Feed algo ${f.algo_id}`}</TableCell>
              <TableCell>{f.feed_type || '—'}</TableCell>
              <TableCell>
                {f.feed_inputs != null
                  ? typeof f.feed_inputs === 'object'
                    ? JSON.stringify(f.feed_inputs)
                    : String(f.feed_inputs)
                  : '—'}
              </TableCell>
              <TableCell>{formatDate(f.created_at)}</TableCell>
              <TableCell>{formatDate(f.updated_at)}</TableCell>
              <TableCell>{f.is_public ? 'Yes' : 'No'}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export default FeedAlgosTable;
